import {
  useCreateWallet,
  useLogin,
  useLoginWithEmail,
  useLoginWithOAuth,
  usePrivy,
  type User,
} from "@privy-io/react-auth";
import { useCallback, useEffect, useRef, useState } from "react";
import { m } from "~/lib/i18n";

export type LoginView = "email-code" | "methods";

export type LoginAuthAction =
  | "email-code"
  | "email-send"
  | "google"
  | "provisioning";

export interface LoginController {
  readonly authAction: LoginAuthAction | null;
  readonly email: string;
  readonly errorMessage: string | null;
  readonly isBusy: boolean;
  readonly ready: boolean;
  readonly verificationCode: string;
  readonly view: LoginView;
  readonly enterWalletLogin: () => void;
  readonly loginWithGoogle: () => Promise<void>;
  readonly resendEmailCode: () => Promise<void>;
  readonly resetToMethods: () => void;
  readonly setEmail: (email: string) => void;
  readonly setVerificationCode: (code: string) => void;
  readonly submitEmail: () => Promise<void>;
  readonly submitVerificationCode: () => Promise<void>;
}

export interface UseLoginControllerOptions {
  readonly onAuthenticated: () => Promise<void> | void;
}

function userHasLinkedWallet(user: User): boolean {
  return user.linkedAccounts.some((account) => account.type === "wallet");
}

export function useLoginController(
  options: UseLoginControllerOptions,
): LoginController {
  const { onAuthenticated } = options;
  const { authenticated, ready, user } = usePrivy();
  const { createWallet } = useCreateWallet();
  const [authAction, setAuthAction] = useState<LoginAuthAction | null>(null);
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [view, setView] = useState<LoginView>("methods");
  const completionStartedRef = useRef(false);

  const finishAuthentication = useCallback(
    async (authenticatedUser: User) => {
      if (completionStartedRef.current) {
        return;
      }

      completionStartedRef.current = true;
      setAuthAction("provisioning");
      setErrorMessage(null);

      try {
        if (!userHasLinkedWallet(authenticatedUser)) {
          await createWallet();
        }
        await onAuthenticated();
      } catch {
        completionStartedRef.current = false;
        setAuthAction(null);
        setErrorMessage(m.login_page_generic_error());
      }
    },
    [createWallet, onAuthenticated],
  );

  const handlePrivyError = useCallback(() => {
    completionStartedRef.current = false;
    setAuthAction(null);
    setErrorMessage(m.login_page_generic_error());
  }, []);

  const emailLogin = useLoginWithEmail({
    onComplete: ({ user: authenticatedUser }) => {
      void finishAuthentication(authenticatedUser);
    },
    onError: handlePrivyError,
  });
  const oauthLogin = useLoginWithOAuth({
    onComplete: ({ user: authenticatedUser }) => {
      void finishAuthentication(authenticatedUser);
    },
    onError: handlePrivyError,
  });
  const walletLogin = useLogin({
    onComplete: ({ user: authenticatedUser }) => {
      void finishAuthentication(authenticatedUser);
    },
    onError: handlePrivyError,
  });

  useEffect(() => {
    if (
      ready &&
      authenticated &&
      user &&
      authAction === null &&
      !completionStartedRef.current
    ) {
      void finishAuthentication(user);
    }
  }, [authAction, authenticated, finishAuthentication, ready, user]);

  const isBusy = authAction !== null || oauthLogin.loading;

  const enterWalletLogin = useCallback(() => {
    setErrorMessage(null);
    walletLogin.login({
      loginMethods: ["wallet"],
      walletChainType: "ethereum-only",
    });
  }, [walletLogin]);

  const submitEmail = useCallback(async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      return;
    }

    setAuthAction("email-send");
    setErrorMessage(null);
    try {
      await emailLogin.sendCode({ email: normalizedEmail });
      setEmail(normalizedEmail);
      setAuthAction(null);
      setView("email-code");
    } catch {
      setAuthAction(null);
      setErrorMessage(m.login_page_generic_error());
    }
  }, [email, emailLogin]);

  const submitVerificationCode = useCallback(async () => {
    if (!/^\d{6}$/u.test(verificationCode)) {
      setErrorMessage(m.login_page_code_invalid_error());
      return;
    }

    setAuthAction("email-code");
    setErrorMessage(null);
    try {
      await emailLogin.loginWithCode({ code: verificationCode });
    } catch {
      setAuthAction(null);
      setErrorMessage(m.login_page_code_invalid_error());
    }
  }, [emailLogin, verificationCode]);

  const loginWithGoogle = useCallback(async () => {
    setAuthAction("google");
    setErrorMessage(null);
    try {
      await oauthLogin.initOAuth({ provider: "google" });
    } catch {
      setAuthAction(null);
      setErrorMessage(m.login_page_generic_error());
    }
  }, [oauthLogin]);

  const resendEmailCode = useCallback(async () => {
    setAuthAction("email-send");
    setErrorMessage(null);
    try {
      await emailLogin.sendCode({ email });
      setAuthAction(null);
    } catch {
      setAuthAction(null);
      setErrorMessage(m.login_page_generic_error());
    }
  }, [email, emailLogin]);

  const resetToMethods = useCallback(() => {
    setView("methods");
    setVerificationCode("");
    setErrorMessage(null);
  }, []);

  return {
    authAction,
    email,
    enterWalletLogin,
    errorMessage,
    isBusy,
    loginWithGoogle,
    ready,
    resendEmailCode,
    resetToMethods,
    setEmail,
    setVerificationCode,
    submitEmail,
    submitVerificationCode,
    verificationCode,
    view,
  };
}
